// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricConfig, BaseMetricEntity, ChartType, ScoreLevel } from './base-metric.entity';

/**
 * Configuration interface for Contributors entity
 */
type ContributorsConfig = BaseMetricConfig;

/**
 * ContributorsEntity - Domain entity for contributors business rules
 *
 * This entity encapsulates the business logic for calculating and determining
 * contributor influence levels based on percentage of contributors employed by the company.
 *
 * Business Rules:
 * - 0.0-0.9% of contributors = Silent (0 points) - Red color, (!) icon
 * - 1.0-4.9% of contributors = Participating (1 point) - Yellow color, line in circle icon
 * - 5.0-19.9% of contributors = Contributing (2 points) - Blue color, thumbs up icon
 * - > 20% of contributors = Leading (3 points) - Green color, thumbs up icon
 */
export class Contributors extends BaseMetricEntity {
  static readonly METRIC_TYPE = 'contributors' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: ContributorsConfig = {
    metricType: 'contributors',
    display: {
      displayName: 'Contributors',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of contributors work at our company?',
        scoringRules: [
          'Each 0.1% of contributors to the project total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 contributors = 0 points',
          '0.000001 - 0.1% of contributors = 1.1 points',
          '0.2% of contributors = 1.2 points',
          '2.5% of contributors = 3.5 points',
          '20.0% of contributors = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company does not employ any contributors for this project.',
      },
      positiveValue: {
        descriptionTemplate: 'Our company employs {percentage}% of contributors to this project.',
      }
    }
  };

  static getMetricConfig() {
    return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for contributors
   */
  static getBottomSummary(value?: string, level?: ScoreLevel): MetricBottomSummary {
    const contributorPercentage = value ? parseFloat(value) : 0;
    const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();
    const bottomSummary = this.CONFIG.bottomSummary;

    if (contributorPercentage === 0) {
      return {
        title: this.CONFIG.display.displayName,
        description: bottomSummary.zeroValue.description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: contributorPercentage.toString()
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }
}
